import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logsSocketHelper } from 'src/app/helpers';
import { contentsActions, eventActions, permissionActions, userActions } from 'src/app/redux/actions';
import { FieldsEnum } from 'src/enums/FieldsEnum';
import { FormEnum } from 'src/enums/form.enum';
import { SocketOperations } from 'src/enums/operator.enum';
import { reduxRootTypes } from 'src/types';
import { ActionResultType } from 'src/types/services/action-service.type';
import { ConfigurationChangeRequestResultType } from 'src/types/services/configuration-change-request-service.type';
import { EventResultType } from 'src/types/services/events-service.type';
import { IncidentFolderResultType } from 'src/types/services/incident-folder-service.type';
import { IncidentResultType } from 'src/types/services/incident-service.type';
import { InjectionResultType } from 'src/types/services/injection-service.type';
import { LineOfEffortResultType } from 'src/types/services/line-of-effort-service.type';
import { LillResultType } from 'src/types/services/lill-service.type';
import { TrainingObjectiveResultType } from 'src/types/services/objective-service.type';
import { ObservationTaskResultType } from 'src/types/services/observation-task-service.type';
import { DecisiveConditionResultType } from 'src/types/services/odc2m/decisive-condition-service.type';
import { EffectResultType } from 'src/types/services/odc2m/effect-service.type';
import { TaskResultType } from 'src/types/services/odc2m/od-task-service.type';
import { PhaseResultType } from 'src/types/services/odc2m/phase-service.type';
import { TurnChartDataType, TurnChartEditDataType } from 'src/types/services/odc2m/turn-chart-service.type';
import { OntologyResultType } from 'src/types/services/ontology-service.type';
import { OutcomeResultType } from 'src/types/services/outcome-service.type';
import { PlanOrderReportProcessResultType } from 'src/types/services/plan-order-report-process-service.type';
import { StateResultType } from 'src/types/services/state-service.type';
import { TechnicalSupportRequestResultType } from 'src/types/services/technical-support-request.service.type';
import { TransactionResultType } from 'src/types/services/transaction-service.type';
import { TroubleTicketResultType } from 'src/types/services/trouble-ticket-service.type';
import { UsersResultType } from 'src/types/services/user-service.type';
import { UserRolePermissionResultType } from 'src/types/services/userRolePermission-service.type';

const EtecubeConnectionHandler = () => {
  const dispatch = useDispatch();

  const { eventId } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);

  const createIncidentFolderByNotification = (incidentFolder: IncidentFolderResultType) => {
    try {
      dispatch(contentsActions.incidentFolderAdded(incidentFolder));
      dispatch(contentsActions.fieldListAdd({ id: incidentFolder.id, fieldsType: FieldsEnum.INCIDENT_FOLDER }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateIncidentFolderByNotification = (incidentFolder: IncidentFolderResultType) => {
    try {
      dispatch(contentsActions.incidentFolderEdited(incidentFolder));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteIncidentFolderByNotification = (incidentFolderId: string) => {
    try {
      dispatch(contentsActions.incidentFolderDelete(incidentFolderId));
    } catch (error) {
      console.log(error);
    }
  };

  const createIncidentByNotification = (incident: IncidentResultType) => {
    try {
      dispatch(contentsActions.incidentAdded(incident));
      dispatch(contentsActions.fieldListAdd({ id: incident.id, fieldsType: FieldsEnum.INCIDENT }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateIncidentByNotification = (incident: IncidentResultType) => {
    try {
      dispatch(contentsActions.incidentEdited(incident));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteIncidentByNotification = (incidentId: string) => {
    try {
      dispatch(contentsActions.incidentDelete(incidentId));
    } catch (error) {
      console.log(error);
    }
  };

  const createInjectionByNotification = (injection: InjectionResultType) => {
    try {
      dispatch(contentsActions.injectionAdded(injection));
      dispatch(contentsActions.fieldListAdd({ id: injection.id, fieldsType: FieldsEnum.INJECTION }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateInjectionByNotification = (injection: InjectionResultType) => {
    try {
      dispatch(contentsActions.injectionEdited(injection));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteInjectionByNotification = (injectionId: string) => {
    try {
      dispatch(contentsActions.injectionDelete(injectionId));
    } catch (error) {
      console.log(error);
    }
  };

  const createActionByNotification = (action: ActionResultType) => {
    try {
      dispatch(contentsActions.actionAdded(action));
      dispatch(contentsActions.fieldListAdd({ id: action.id, fieldsType: FieldsEnum.ACTION }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateActionByNotification = (action: ActionResultType) => {
    try {
      dispatch(contentsActions.actionEdited(action));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteActionByNotification = (actionId: string) => {
    try {
      dispatch(contentsActions.actionDelete(actionId));
    } catch (error) {
      console.log(error);
    }
  };
  const createOutcomeByNotification = (outcome: OutcomeResultType) => {
    try {
      dispatch(contentsActions.outcomeAdded(outcome));
      dispatch(contentsActions.fieldListAdd({ id: outcome.id, fieldsType: FieldsEnum.OUTCOME }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateOutcomeByNotification = (outcome: OutcomeResultType) => {
    try {
      dispatch(contentsActions.outcomeEdited(outcome));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteOutcomeByNotification = (outcomeId: string) => {
    try {
      dispatch(contentsActions.outcomeDelete(outcomeId));
    } catch (error) {
      console.log(error);
    }
  };
  const createObservationTaskByNotification = (observationTask: ObservationTaskResultType) => {
    try {
      dispatch(contentsActions.observationTaskAdded(observationTask));
      dispatch(contentsActions.fieldListAdd({ id: observationTask.id, fieldsType: FieldsEnum.OBSERVATION_TASK }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateObservationTaskByNotification = (observationTask: ObservationTaskResultType) => {
    try {
      dispatch(contentsActions.observationTaskEdited(observationTask));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteObservationTaskByNotification = (observationTaskId: string) => {
    try {
      dispatch(contentsActions.observationTaskDelete(observationTaskId));
    } catch (error) {
      console.log(error);
    }
  };

  const createTrainingObjectiveByNotification = (trainingObjective: TrainingObjectiveResultType) => {
    try {
      dispatch(contentsActions.trainingObjectiveAdded(trainingObjective));
      dispatch(contentsActions.fieldListAdd({ id: trainingObjective.id, fieldsType: FieldsEnum.OBJECTIVE }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateTrainingObjectiveByNotification = (trainingObjective: TrainingObjectiveResultType) => {
    try {
      dispatch(contentsActions.trainingObjectiveEdited(trainingObjective));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteTrainingObjectiveByNotification = (trainingObjectiveId: string) => {
    try {
      dispatch(contentsActions.trainingObjectiveDelete(trainingObjectiveId));
    } catch (error) {
      console.log(error);
    }
  };

  const createPhaseByNotification = (phase: PhaseResultType) => {
    try {
      dispatch(contentsActions.phaseAdded(phase));
      dispatch(contentsActions.fieldListAdd({ id: phase.id, fieldsType: FieldsEnum.PHASE }));
    } catch (error) {
      console.log(error);
    }
  };

  const updatePhaseByNotification = (phase: PhaseResultType) => {
    try {
      dispatch(contentsActions.phaseEdited(phase));
    } catch (error) {
      console.log(error);
    }
  };

  const deletePhaseByNotification = (phaseId: string) => {
    try {
      dispatch(contentsActions.phaseDeleted(phaseId));
    } catch (error) {
      console.log(error);
    }
  };

  const createLineOfEffortByNotification = (lineOfEffort: LineOfEffortResultType) => {
    try {
      dispatch(contentsActions.lineOfEffortAdded(lineOfEffort));
      dispatch(contentsActions.fieldListAdd({ id: lineOfEffort.id, fieldsType: FieldsEnum.LINE_OF_EFFORT }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateLineOfEffortByNotification = (lineOfEffort: LineOfEffortResultType) => {
    try {
      dispatch(contentsActions.lineOfEffortEdited(lineOfEffort));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteLineOfEffortByNotification = (lineOfEffortId: string) => {
    try {
      dispatch(contentsActions.lineOfEffortDeleted(lineOfEffortId));
    } catch (error) {
      console.log(error);
    }
  };

  const createPlanOrderReportProcessByNotification = (planOrderReportProcess: PlanOrderReportProcessResultType) => {
    try {
      dispatch(contentsActions.planOrderReportProcessAdded(planOrderReportProcess));
      dispatch(
        contentsActions.fieldListAdd({
          id: planOrderReportProcess.id,
          fieldsType: FieldsEnum.PLAN_ORDER_REPORT_PROCESS,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const updatePlanOrderReportProcessByNotification = (planOrderReportProcess: PlanOrderReportProcessResultType) => {
    try {
      dispatch(contentsActions.planOrderReportProcessEdited(planOrderReportProcess));
    } catch (error) {
      console.log(error);
    }
  };

  const deletePlanOrderReportProcessByNotification = (planOrderReportProcessId: string) => {
    try {
      dispatch(contentsActions.planOrderReportProcessDeleted(planOrderReportProcessId));
    } catch (error) {
      console.log(error);
    }
  };

  const createDecisiveConditionByNotification = (decisiveCondition: DecisiveConditionResultType) => {
    try {
      dispatch(contentsActions.decisiveConditionAdded(decisiveCondition));
      dispatch(contentsActions.fieldListAdd({ id: decisiveCondition.id, fieldsType: FieldsEnum.DECISIVE_CONDITIONS }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateDecisiveConditionByNotification = (decisiveCondition: DecisiveConditionResultType) => {
    try {
      dispatch(contentsActions.decisiveConditionEdited(decisiveCondition));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteDecisiveConditionByNotification = (decisiveConditionId: string) => {
    try {
      dispatch(contentsActions.decisiveConditionDeleted(decisiveConditionId));
    } catch (error) {
      console.log(error);
    }
  };

  const createODTaskByNotification = (odTask: TaskResultType) => {
    try {
      dispatch(contentsActions.odTaskAdded(odTask));
      dispatch(contentsActions.fieldListAdd({ id: odTask.id, fieldsType: FieldsEnum.OD_TASK }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateODTaskByNotification = (odTask: TaskResultType) => {
    try {
      dispatch(contentsActions.odTaskEdited(odTask));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteODTaskByNotification = (odTaskId: string) => {
    try {
      dispatch(contentsActions.odTaskDeleted(odTaskId));
    } catch (error) {
      console.log(error);
    }
  };

  const createEffectByNotification = (effect: EffectResultType) => {
    try {
      dispatch(contentsActions.effectAdded(effect));
      dispatch(contentsActions.fieldListAdd({ id: effect.id, fieldsType: FieldsEnum.EFFECT }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateEffectByNotification = (effect: EffectResultType) => {
    try {
      dispatch(contentsActions.effectEdited(effect));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteEffectByNotification = (effectId: string) => {
    try {
      dispatch(contentsActions.effectDeleted(effectId));
    } catch (error) {
      console.log(error);
    }
  };

  const createTurnChartByNotification = (turnChart: TurnChartDataType) => {
    try {
      dispatch(contentsActions.turnChartAdded(turnChart));
    } catch (error) {
      console.log(error);
    }
  };

  const updateTurnChartByNotification = (turnChart: TurnChartEditDataType[]) => {
    try {
      dispatch(contentsActions.turnChartEdited(turnChart));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteTurnChartByNotification = (turnChartId: string) => {
    try {
      dispatch(contentsActions.turnChartDeleted(turnChartId));
    } catch (error) {
      console.log(error);
    }
  };

  const createCCRByNotification = (configurationChangeRequest: ConfigurationChangeRequestResultType) => {
    try {
      dispatch(contentsActions.configurationChangeRequestAdded(configurationChangeRequest));
      dispatch(
        contentsActions.fieldListAdd({
          id: configurationChangeRequest.id,
          fieldsType: FieldsEnum.CONFIGURATION_CHANGE_REQUEST,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const updateCCRByNotification = (configurationChangeRequest: ConfigurationChangeRequestResultType) => {
    try {
      dispatch(contentsActions.configurationChangeRequestEdited(configurationChangeRequest));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteCCRByNotification = (ccrId: string) => {
    try {
      dispatch(contentsActions.configurationChangeRequestDelete(ccrId));
    } catch (error) {
      console.log(error);
    }
  };

  const createTTByNotification = (troubleTicket: TroubleTicketResultType) => {
    try {
      dispatch(contentsActions.troubleTicketAdded(troubleTicket));
      dispatch(contentsActions.fieldListAdd({ id: troubleTicket.id, fieldsType: FieldsEnum.TROUBLE_TICKET }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateTTByNotification = (troubleTicket: TroubleTicketResultType) => {
    try {
      dispatch(contentsActions.troubleTicketEdited(troubleTicket));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteTTByNotification = (ttId: string) => {
    try {
      dispatch(contentsActions.troubleTicketDelete(ttId));
    } catch (error) {
      console.log(error);
    }
  };

  const createTSRByNotification = (technicalSupportRequest: TechnicalSupportRequestResultType) => {
    try {
      dispatch(contentsActions.technicalSupportRequestAdded(technicalSupportRequest));

      dispatch(
        contentsActions.fieldListAdd({
          id: technicalSupportRequest.id,
          fieldsType: FieldsEnum.TECHNICAL_SUPPORT_REQUEST,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const updateTSRByNotification = (technicalSupportRequest: TechnicalSupportRequestResultType) => {
    try {
      dispatch(contentsActions.technicalSupportRequestEdited(technicalSupportRequest));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteTSRByNotification = (tsrId: string) => {
    try {
      dispatch(contentsActions.technicalSupportRequestDeleted(tsrId));
    } catch (error) {
      console.log(error);
    }
  };

  const createLILLByNotification = (lill: LillResultType) => {
    try {
      dispatch(contentsActions.lillAdded(lill));

      dispatch(contentsActions.fieldListAdd({ id: lill.id, fieldsType: FieldsEnum.LILL }));
    } catch (error) {
      console.log(error);
    }
  };

  const updateLILLByNotification = (lill: LillResultType) => {
    try {
      dispatch(contentsActions.lillEdited(lill));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteLILLByNotification = (lillId: string) => {
    try {
      dispatch(contentsActions.lillDelete(lillId));
    } catch (error) {
      console.log(error);
    }
  };

  const createOntologyByNotification = (ontology: OntologyResultType) => {
    try {
      dispatch(contentsActions.ontologyAdded(ontology));
    } catch (error) {
      console.log(error);
    }
  };

  const updateOntologyByNotification = (ontology: OntologyResultType) => {
    try {
      dispatch(contentsActions.ontologyEdited(ontology));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteOntologyByNotification = (ontologyId: string) => {
    try {
      dispatch(contentsActions.ontologyDeleted(ontologyId));
    } catch (error) {
      console.log(error);
    }
  };
  const createEventByNotification = (event: EventResultType) => {
    try {
      dispatch(eventActions.eventAdded(event));
    } catch (error) {
      console.log(error);
    }
  };

  const updateEventByNotification = (event: EventResultType) => {
    try {
      dispatch(eventActions.eventEdited(event));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteEventByNotification = (evntId: string) => {
    try {
      dispatch(eventActions.eventDeleted(evntId));
    } catch (error) {
      console.log(error);
    }
  };
  const createUsersByNotification = (users: UsersResultType) => {
    try {
      dispatch(userActions.setAddInformation(users));
    } catch (error) {
      console.log(error);
    }
  };

  const updateUsersByNotification = (users: UsersResultType) => {
    try {
      dispatch(userActions.setUserEdited(users));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteUsersByNotification = (userId: string) => {
    try {
      dispatch(userActions.userDeleted(userId));
    } catch (error) {
      console.log(error);
    }
  };
  const createUserPermissionByNotification = (permission: UserRolePermissionResultType[]) => {
    try {
      dispatch(permissionActions.userRolePermissionAdded(permission));
    } catch (error) {
      console.log(error);
    }
  };

  const updateUserPermissionByNotification = (permission: UserRolePermissionResultType[]) => {
    try {
      dispatch(permissionActions.userRolePermissionEdited(permission));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteUserPermissionByNotification = (userId: string) => {
    try {
      dispatch(permissionActions.userRolePermissionDeleted(userId));
    } catch (error) {
      console.log(error);
    }
  };

  const createStateByNotification = (state: StateResultType) => {
    try {
      dispatch(contentsActions.stateAdded(state));
    } catch (error) {
      console.log(error);
    }
  };
  const createTransactionByNotification = (transaction: TransactionResultType) => {
    try {
      dispatch(
        contentsActions.transactionAdded({
          ...transaction,
          fromStateId: transaction.fromStateId,
          toStateId: transaction.toStateId,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const deleteTransactionByNotification = (transactionId: string) => {
    try {
      dispatch(contentsActions.transactionDelete(transactionId));
    } catch (error) {
      console.log(error);
    }
  };

  const createLogSocketConnection = () => {
    logsSocketHelper.createConnection();

    logsSocketHelper.listenSocket(
      `${FormEnum.Incident_Folder.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createIncidentFolderByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateIncidentFolderByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteIncidentFolderByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Incident.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createIncidentByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateIncidentByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteIncidentByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Injection.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createInjectionByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateInjectionByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteInjectionByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Action.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createActionByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateActionByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteActionByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Outcome.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createOutcomeByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateOutcomeByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteOutcomeByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Observation_Task.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createObservationTaskByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateObservationTaskByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteObservationTaskByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Objective.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createTrainingObjectiveByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateTrainingObjectiveByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteTrainingObjectiveByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Phase.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createPhaseByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updatePhaseByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deletePhaseByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Line_Of_Effort.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createLineOfEffortByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateLineOfEffortByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteLineOfEffortByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Plan_Order_Report_Process.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createPlanOrderReportProcessByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updatePlanOrderReportProcessByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deletePlanOrderReportProcessByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Decisive_Conditions.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createDecisiveConditionByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateDecisiveConditionByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteDecisiveConditionByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.OD_Task.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createODTaskByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateODTaskByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteODTaskByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Effect.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createEffectByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateEffectByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteEffectByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Turn_Chart.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createTurnChartByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateTurnChartByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteTurnChartByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Configuration_Change_Request.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createCCRByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateCCRByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteCCRByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Trouble_Ticket.toString()}_${eventId}`,

      (data: { operationType: SocketOperations; payload: any }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createTTByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateTTByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteTTByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Technical_Support_Request.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createTSRByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateTSRByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteTSRByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Lill}`.toString(),
      (data: { operationType: SocketOperations; payload: any }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createLILLByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateLILLByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteLILLByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      FormEnum.Ontology.toString(),
      (data: { operationType: SocketOperations; payload: any }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createOntologyByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateOntologyByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteOntologyByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      FormEnum.Event.toString(),
      (data: { operationType: SocketOperations; payload: any }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createEventByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateEventByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteEventByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      FormEnum.Users.toString(),
      (data: { operationType: SocketOperations; payload: any }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createUsersByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateUsersByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteUsersByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      FormEnum.User_Rights.toString(),
      (data: { operationType: SocketOperations; payload: any }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createUserPermissionByNotification(data.payload);
            break;
          case SocketOperations.Update:
            updateUserPermissionByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteUserPermissionByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.State.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createStateByNotification(data.payload);
            break;
          // case SocketOperations.Update:
          //   updateStateByNotification(data.payload);
          //   break;
          default:
            break;
        }
      },
    );
    logsSocketHelper.listenSocket(
      `${FormEnum.Transaction.toString()}_${eventId}`,
      (data: { operationType: SocketOperations; payload: any; eventId: string }) => {
        switch (data.operationType) {
          case SocketOperations.Create:
            createTransactionByNotification(data.payload);
            break;
          case SocketOperations.Delete:
            deleteTransactionByNotification(data.payload);
            break;
          default:
            break;
        }
      },
    );
  };

  useEffect(() => {
    if (eventId) {
      createLogSocketConnection();
    }
  }, [eventId]);

  return null;
};

export default EtecubeConnectionHandler;
